/**
 * Daily Welcome Modal - A beautiful, dopamine-inducing welcome experience
 * Shows once per day when the user first logs in
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Button,
  IconButton,
  Fade,
  useTheme,
  useMediaQuery,
  Grow,
  Paper
} from '@mui/material';
import {
  VolumeUp,
  VolumeOff,
  Close,
  Favorite,
  Star,
  AutoAwesome,
  WbSunny
} from '@mui/icons-material';
import { ttsService } from '../services/ttsService';

const DailyWelcomeModal = ({ 
  open, 
  onClose, 
  greeting, 
  userName, 
  gender = 'female',
  isSoundEnabled = true 
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [isPlaying, setIsPlaying] = useState(false);
  const [showStars, setShowStars] = useState(false);

  // Debug logging for props
  console.log('🔍 DailyWelcomeModal props:', { 
    open, 
    onClose: typeof onClose, 
    greeting: greeting?.substring(0, 50), 
    userName, 
    gender, 
    isSoundEnabled 
  });

  // Trigger star animation after modal opens
  useEffect(() => {
    if (open) {
      const timer = setTimeout(() => setShowStars(true), 500);
      return () => clearTimeout(timer);
    } else {
      setShowStars(false);
    }
  }, [open]);

  const handlePlayAudio = async () => {
    if (isPlaying || !isSoundEnabled || !greeting) return;
    
    try {
      setIsPlaying(true);
      await ttsService.speakText(greeting, { gender });
    } catch (error) {
      console.error('Welcome TTS error:', error);
    } finally {
      setIsPlaying(false);
    }
  };

  const handleClose = () => {
    console.log('🔴 DailyWelcomeModal: Close button clicked');
    if (isPlaying) {
      console.log('🔴 Stopping TTS audio');
      ttsService.stop();
      setIsPlaying(false);
    }
    console.log('🔴 Calling onClose callback');
    if (typeof onClose === 'function') {
      onClose();
    } else {
      console.error('🚨 onClose is not a function:', typeof onClose, onClose);
    }
  };

  // Fun floating icons
  const FloatingIcon = ({ Icon, delay = 0, position }) => (
    <Fade in={showStars} timeout={1000} style={{ transitionDelay: `${delay}ms` }}>
      <Box
        sx={{
          position: 'absolute',
          ...position,
          animation: 'float 3s ease-in-out infinite',
          animationDelay: `${delay}ms`,
          color: 'rgba(255, 215, 0, 0.8)',
          fontSize: '1.5rem',
          zIndex: 0, // Ensure floating icons are behind interactive elements
          pointerEvents: 'none', // Prevent them from blocking clicks
          '@keyframes float': {
            '0%, 100%': { transform: 'translateY(0px)' },
            '50%': { transform: 'translateY(-10px)' }
          }
        }}
      >
        <Icon fontSize="inherit" />
      </Box>
    </Fade>
  );

  const getTimeBasedEmoji = () => {
    const hour = new Date().getHours();
    if (hour < 12) return '🌅';
    if (hour < 17) return '☀️';
    if (hour < 20) return '🌇';
    return '🌙';
  };

  const getTimeBasedGreeting = () => {
    const hour = new Date().getHours();
    const companionName = gender === 'female' ? 'Sarah' : 'Mike';
    
    if (hour < 12) return `Good morning! It's ${companionName} 💫`;
    if (hour < 17) return `Good afternoon! It's ${companionName} ✨`;
    if (hour < 20) return `Good evening! It's ${companionName} 🌟`;
    return `Good evening! It's ${companionName} 🌙`;
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%)',
          borderRadius: '24px',
          overflow: 'hidden',
          position: 'relative',
          minHeight: '400px',
          boxShadow: '0 20px 60px rgba(102, 126, 234, 0.3)'
        }
      }}
      TransitionComponent={Grow}
      transitionDuration={600}
    >
      {/* Floating decorative elements */}
      <FloatingIcon Icon={Star} delay={200} position={{ top: '15%', left: '10%' }} />
      <FloatingIcon Icon={AutoAwesome} delay={400} position={{ top: '20%', right: '15%' }} />
      <FloatingIcon Icon={Favorite} delay={600} position={{ bottom: '25%', left: '15%' }} />
      <FloatingIcon Icon={WbSunny} delay={800} position={{ bottom: '30%', right: '10%' }} />

      {/* Close button */}
      <IconButton
        onClick={handleClose}
        sx={{
          position: 'absolute',
          top: 12,
          right: 12,
          color: 'rgba(255, 255, 255, 0.8)',
          bgcolor: 'rgba(255, 255, 255, 0.1)',
          backdropFilter: 'blur(10px)',
          '&:hover': {
            bgcolor: 'rgba(255, 255, 255, 0.2)',
            color: 'white'
          },
          zIndex: 10 // Increased zIndex to ensure it's always on top
        }}
      >
        <Close />
      </IconButton>

      <DialogContent
        sx={{
          p: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          minHeight: '400px',
          position: 'relative'
        }}
      >
        {/* Main content */}
        <Fade in={open} timeout={800} style={{ transitionDelay: '200ms' }}>
          <Box sx={{ px: 4, py: 2, zIndex: 1 }}>
            {/* Time-based emoji */}
            <Typography
              variant="h1"
              sx={{
                fontSize: '4rem',
                mb: 2,
                filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.2))'
              }}
            >
              {getTimeBasedEmoji()}
            </Typography>

            {/* Welcome header */}
            <Typography
              variant="h4"
              sx={{
                color: 'white',
                fontWeight: 700,
                mb: 1,
                textShadow: '0 2px 4px rgba(0,0,0,0.3)',
                fontSize: { xs: '1.5rem', sm: '2rem' }
              }}
            >
              {getTimeBasedGreeting()}
            </Typography>

            {/* User name if available */}
            {userName && (
              <Typography
                variant="h6"
                sx={{
                  color: 'rgba(255, 255, 255, 0.9)',
                  fontWeight: 500,
                  mb: 3,
                  textShadow: '0 1px 2px rgba(0,0,0,0.2)'
                }}
              >
                Welcome back, {userName}! 💕
              </Typography>
            )}

            {/* Main greeting content */}
            {greeting && (
              <Paper
                elevation={0}
                sx={{
                  bgcolor: 'rgba(255, 255, 255, 0.95)',
                  backdropFilter: 'blur(20px)',
                  borderRadius: '20px',
                  p: 3,
                  mb: 3,
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                  border: '1px solid rgba(255, 255, 255, 0.2)'
                }}
              >
                <Typography
                  variant="body1"
                  sx={{
                    color: '#2d3748',
                    lineHeight: 1.6,
                    fontSize: { xs: '1rem', sm: '1.1rem' },
                    fontWeight: 500
                  }}
                >
                  {greeting}
                </Typography>
              </Paper>
            )}

            {/* Action buttons */}
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              {/* Audio button */}
              {greeting && isSoundEnabled && (
                <Button
                  variant="contained"
                  onClick={handlePlayAudio}
                  disabled={isPlaying}
                  startIcon={isPlaying ? <VolumeOff /> : <VolumeUp />}
                  sx={{
                    bgcolor: 'rgba(255, 255, 255, 0.2)',
                    backdropFilter: 'blur(10px)',
                    color: 'white',
                    borderRadius: '25px',
                    px: 3,
                    py: 1.5,
                    fontWeight: 600,
                    textTransform: 'none',
                    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)',
                    border: '1px solid rgba(255, 255, 255, 0.3)',
                    '&:hover': {
                      bgcolor: 'rgba(255, 255, 255, 0.3)',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 6px 20px rgba(0, 0, 0, 0.15)'
                    },
                    '&:disabled': {
                      bgcolor: 'rgba(255, 255, 255, 0.1)',
                      color: 'rgba(255, 255, 255, 0.6)'
                    },
                    transition: 'all 0.3s ease'
                  }}
                >
                  {isPlaying ? 'Playing...' : 'Listen'}
                </Button>
              )}

              {/* Continue button */}
              <Button
                variant="contained"
                onClick={handleClose}
                sx={{
                  bgcolor: 'rgba(255, 255, 255, 0.9)',
                  color: '#667eea',
                  borderRadius: '25px',
                  px: 4,
                  py: 1.5,
                  fontWeight: 700,
                  textTransform: 'none',
                  boxShadow: '0 4px 16px rgba(255, 255, 255, 0.2)',
                  '&:hover': {
                    bgcolor: 'white',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 6px 20px rgba(255, 255, 255, 0.3)'
                  },
                  transition: 'all 0.3s ease'
                }}
              >
                Let's Chat! ✨
              </Button>
            </Box>
          </Box>
        </Fade>

        {/* Background decoration */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            opacity: 0.1,
            background: `
              radial-gradient(circle at 20% 20%, rgba(255,255,255,0.3) 0%, transparent 50%),
              radial-gradient(circle at 80% 80%, rgba(255,255,255,0.2) 0%, transparent 50%),
              radial-gradient(circle at 40% 60%, rgba(255,255,255,0.1) 0%, transparent 50%)
            `,
            zIndex: 0
          }}
        />
      </DialogContent>
    </Dialog>
  );
};

export default DailyWelcomeModal;
